import get from 'lodash/get'
import { v1 as uuidv1 } from 'uuid'

import { parseHtml } from '../shared/parseHtml'
import { CONSENT_CHANGED_EVENT, getConsent } from './_cookie-consent'
import { dispatcher } from './dispatcher'
import { CookieConsent } from './module/cookie_consent/model/CookieConsent'

const CODE_TYPE_ANALYTICS = 'analytics'
const CODE_TYPE_MARKETING = 'marketing'
const CODE_LOCATION_HEAD = 'head'
const CODE_LOCATION_CLOSING_BODY = 'closing_body'

const injectedCodes = new Set()
let listenerAdded = false

/**
 * @param {Object|null} code
 */
function normalizeCode(code) {
  if (!code || typeof code !== 'object') {
    return null
  }

  const id = code.id || uuidv1()
  const type = code.type === CODE_TYPE_ANALYTICS ? CODE_TYPE_ANALYTICS : CODE_TYPE_MARKETING
  const location = code.location === CODE_LOCATION_HEAD ? CODE_LOCATION_HEAD : CODE_LOCATION_CLOSING_BODY
  const html = typeof code.html === 'string' ? code.html : ''

  return {
    id,
    type,
    location,
    html,
  }
}

/**
 * @return {Array<{
 *   id: String,
 *   type: String,
 *   location: String,
 *   html: String,
 * }>}
 */
function getCodes() {
  const rawCodes = get(window, ['APP', 'analytics', 'codes'], [])
  if (!Array.isArray(rawCodes)) {
    return []
  }

  return rawCodes.map(normalizeCode).filter((code) => code && code.html.trim() !== '')
}

function cloneScriptElement(scriptNode) {
  const clonedScript = document.createElement('script')
  Array.from(scriptNode.attributes).forEach((attribute) => {
    clonedScript.setAttribute(attribute.name, attribute.value)
  })

  if (scriptNode.textContent) {
    clonedScript.textContent = scriptNode.textContent
  }

  return clonedScript
}

function hasAttribute(element, attributeName) {
  if (typeof element.hasAttribute === 'function') {
    return element.hasAttribute(attributeName)
  }

  return element.getAttribute(attributeName) !== null
}

function isBlockingExternalClassicScript(scriptNode) {
  const src = scriptNode.getAttribute('src')
  if (!src) {
    return false
  }

  const type = (scriptNode.getAttribute('type') || '').trim().toLowerCase()
  if (type === 'module') {
    return false
  }

  return !hasAttribute(scriptNode, 'async') && !hasAttribute(scriptNode, 'defer')
}

function waitForScriptLoad(scriptNode) {
  if (!isBlockingExternalClassicScript(scriptNode)) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    scriptNode.addEventListener('load', resolve, { once: true })
    scriptNode.addEventListener('error', resolve, { once: true })
  })
}

async function appendNodeSequentially(target, node) {
  if (node.nodeType !== 1) {
    target.appendChild(node.cloneNode(true))
    return
  }

  if (node.tagName.toLowerCase() === 'script') {
    const clonedScript = cloneScriptElement(node)
    target.appendChild(clonedScript)
    await waitForScriptLoad(clonedScript)
    return
  }

  const clonedElement = node.cloneNode(false)
  target.appendChild(clonedElement)

  for (const childNode of Array.from(node.childNodes)) {
    await appendNodeSequentially(clonedElement, childNode)
  }
}

async function injectCodeHtml(code) {
  const target = code.location === CODE_LOCATION_HEAD ? document.head : document.body

  for (const node of parseHtml(code.html, { executableScripts: true })) {
    await appendNodeSequentially(target, node)
  }
}

/**
 * @param {CookieConsent|null} consent
 */
function renderCodes(consent) {
  const pendingInjections = []

  getCodes().forEach((code) => {
    if (injectedCodes.has(code.id)) {
      return
    }

    if (code.type === CODE_TYPE_ANALYTICS && (!consent || !consent.analytics)) {
      return
    }

    if (code.type === CODE_TYPE_MARKETING && (!consent || !consent.marketing)) {
      return
    }

    pendingInjections.push(injectCodeHtml(code))
    injectedCodes.add(code.id)
  })

  return Promise.allSettled(pendingInjections)
}

function setupAnalyticsCodes() {
  void renderCodes(getConsent())

  if (listenerAdded) {
    return
  }

  dispatcher.addListener(CONSENT_CHANGED_EVENT, (ev) => {
    void renderCodes(ev.consent)
  })
  listenerAdded = true
}

export { setupAnalyticsCodes }
